import type { WhatsNewCover } from '@/components/atoms/WhatsNew';

/**
 * The latest What's New entries for the dashboard and the sidebar's bell. The
 * full changelog lives in `atoms/WhatsNew.tsx` (~100 KB, mostly HTML
 * previews), so it is a separate chunk, loaded when the browser is idle; no
 * app page imports it statically. The dashboard shows a few cards instead of
 * rendering all the entries, as it used to.
 */
export interface WhatsNewItem {
  /** The untranslated `sender`, which is also its `whats_new` translation key. */
  key: string;
  href: string;
  openInNewTab?: boolean;
  cover?: string;
  /** Drawn in code instead of `cover`. */
  drawn?: WhatsNewCover;
}

const LATEST_COUNT = 8;

let pending: Promise<WhatsNewItem[]> | null = null;

export function loadWhatsNew(): Promise<WhatsNewItem[]> {
  if (!pending) {
    pending = import('@/components/atoms/WhatsNew')
      .then(({ messages }) =>
        messages.slice(0, LATEST_COUNT).map((message) => ({
          key: message.sender,
          href: message.href,
          openInNewTab: message.openInNewTab,
          cover: message.image || message.poster || message.sampleImages?.[0],
          drawn: message.dashboardCover,
        })),
      )
      .catch((error) => {
        // Let a later caller retry, e.g. after a flaky network.
        pending = null;
        throw error;
      });
  }
  return pending;
}

const LEADING_SYMBOLS = /^[^\p{L}\p{N}]+/u;
// Variation selector-16 and zero-width joiner, as escapes so they stay visible.
const LEADING_EMOJI = new RegExp(
  '^\\p{Extended_Pictographic}(?:\\uFE0F|\\u200D\\p{Extended_Pictographic})*',
  'u',
);
const SUBTITLE_DASH = /\s+[—–-]\s+/;

/** "🎙️ New: Voice Mode for AI Agents — Your Agent Can Talk Now" → its parts. */
export function splitWhatsNewTitle(title: string): {
  emoji?: string;
  title: string;
  subtitle?: string;
} {
  const emoji = title.match(LEADING_EMOJI)?.[0];
  let rest = title.replace(LEADING_SYMBOLS, '');
  // Drop a short label before the first colon ("New:", "Nouveau :", "新功能：").
  const colon = rest.search(/[:：]/);
  if (colon > 0 && colon <= 12) rest = rest.slice(colon + 1).trimStart();
  const [head, ...tail] = rest.split(SUBTITLE_DASH);
  return {
    emoji,
    title: head.replace(/!+$/, ''),
    subtitle: tail.length ? tail.join(' — ').replace(/!+$/, '') : undefined,
  };
}
